import { displayDate } from "src/utils/dateHandler.util";
import { PaymentTransaction } from "../types/payment-transaction.entity";
import { Link, useLocation } from "react-router-dom";
import { FaExternalLinkAlt } from "react-icons/fa";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";

type Props = {
  data: (PaymentTransaction & { id: string })[];
};

const statusBadgeClass = (status: PaymentTransaction["paymentStatus"]) => {
  if (!status) return "bg-gray-100 text-gray-700 border-gray-200";
  
  switch (status) {
    case "INITIALIZED":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "SUCCEEDED":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "CANCELED":
      return "bg-gray-100 text-gray-700 border-gray-200";
    case "FAILED":
      return "bg-red-100 text-red-700 border-red-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const serviceBadgeClass = (service: PaymentTransaction["PaymentService"]) => {
  if (!service) return "bg-gray-100 text-gray-700 border-gray-200";
  
  switch (service) {
    case "STRIPE":
      return "bg-purple-100 text-purple-700 border-purple-200";
    case "APPLE_PAY":
      return "bg-black text-white border-black";
    case "GOOGLE_PAY":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "WIX_PAYMENT":
      return "bg-green-100 text-green-700 border-green-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

export const PaymentTransactionsTable: React.FC<Props> = ({ data }) => {
  const { search } = useLocation();

  return (
    <div className="overflow-x-auto mb-6">
      <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
        <thead className="bg-gray-900 text-white text-center">
          <tr>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Transaction ID</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">User</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Status</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Service</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Payment Amount</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Wallet Amount</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Created</th>
            <th className="px-4 py-3 uppercase text-xs tracking-wider">Actions</th>
          </tr>
        </thead>

        <tbody>
          {(!data || data.length === 0) && (
            <tr>
              <td colSpan={8} className="text-center py-4 text-xl text-gray-500">
                No Payment Transactions
              </td>
            </tr>
          )}

          {data && data.map((transaction, i) => {
            const createdAt = transaction.createdAt instanceof Date ? transaction.createdAt : (transaction.createdAt as any).toDate?.() ?? undefined;
            return (
              <tr
                key={(transaction as any).id ?? (transaction as any).transactionId ?? `transaction-row-${i}`}
                className="text-center odd:bg-white even:bg-gray-50 hover:bg-gray-100"
              >
                <td className="px-4 py-3 border-t border-gray-200 font-mono text-xs" title={transaction.id || "No ID"}>
                  {transaction.id || "No ID"}
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  {transaction.userId ? (
                    <Link
                      to={`/users/${transaction.userId}`}
                      className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                    >
                      {transaction.userId}
                    </Link>
                  ) : (
                    <span className="text-gray-500">No User ID</span>
                  )}
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <span className={`inline-block px-2 py-1 rounded-full border text-xs font-semibold ${statusBadgeClass(
                    transaction.paymentStatus
                  )}`}>
                    {transaction.paymentStatus || "Unknown"}
                  </span>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <span className={`inline-block px-2 py-1 rounded-full border text-xs font-semibold ${serviceBadgeClass(
                    transaction.PaymentService
                  )}`}>
                    {transaction.PaymentService ? transaction.PaymentService.replace("_", " ") : "Unknown"}
                  </span>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span className="font-medium">{transaction.paymentAmount || "N/A"}</span>
                    <span className="text-xs text-gray-500">{transaction.paymentCurrency || "N/A"}</span>
                  </div>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span className="font-medium">{transaction.walletAmount || "N/A"}</span>
                    <span className="text-xs text-gray-500">{transaction.walletCurrency || "N/A"}</span>
                  </div>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span>{createdAt ? createdAt.toLocaleDateString() : (transaction.createdAt ? displayDate(transaction.createdAt) : "N/A")}</span>
                    <span className="text-xs text-gray-500">{createdAt ? createdAt.toLocaleTimeString() : ""}</span>
                  </div>
                </td>
                <td className="px-4 py-3 border-t border-gray-200">
                  {transaction.id ? (
                    <Link
                      to={appendSearchToPath(`/payment-transactions/${transaction.id}`, search)}
                      className="inline-flex items-center justify-center px-4 py-2 font-medium rounded-lg shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white hover:shadow-lg transform hover:scale-105 focus:ring-emerald-300"
                    >
                      <FaExternalLinkAlt />
                    </Link>
                  ) : (
                    <span className="inline-flex items-center justify-center px-4 py-2 font-medium rounded-lg shadow-md bg-gray-300 text-gray-500 cursor-not-allowed">
                      <FaExternalLinkAlt />
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PaymentTransactionsTable;
